import React from "react";
import RangeBar from "./RangeBar";

const DAYS = ["L", "M", "X", "J", "V", "S", "D"];

/**
 * MiniRangeChart
 * A compact 7-day range bar chart with optional week-over-week comparison.
 * Each day column renders one or two RangeBars (previous vs current week).
 */
const MiniRangeChart = ({
  s1,
  s2,
  color1,
  color2,
  domainMin,
  domainMax,
  tickFn,
  tooltipFn,
  showComparison,
  dateRange,
}) => {
  const hasData = (data) =>
    data && (data[0] !== 0 || data[1] !== 0 || data[2] !== 0);
  const formatTooltip = tooltipFn ?? ((v) => v);
  const range = domainMax - domainMin || 1;
  const ticks = [0, 0.5, 1].map((t) => domainMin + t * range);

  const formatDateStr = (dateObj) => {
    if (!dateObj) return "";
    const str = dateObj.format("D MMM");
    const parts = str.split(" ");
    if (parts.length === 2 && parts[1]) {
      const monthClean = parts[1].replace(".", "");
      const monthCap = monthClean.charAt(0).toUpperCase() + monthClean.slice(1);
      return `${parts[0]} ${monthCap}`;
    }
    return str;
  };

  return (
    <div className="flex gap-2 items-end" style={{ height: 120 }}>
      {/* Y-axis ticks */}
      <div
        className="flex flex-col justify-between h-full text-[9px] text-slate-400 text-right pr-2 select-none"
        style={{ minWidth: 32 }}
      >
        {ticks.reverse().map((t, i) => (
          <span key={i} className="font-semibold">
            {tickFn(t)}
          </span>
        ))}
      </div>

      {/* Day columns */}
      <div className="flex flex-1 gap-2 items-end h-full">
        {DAYS.map((day, i) => {
          const s1Data = s1[i] || [domainMin, domainMin, domainMin];
          const s2Data = s2[i] || [domainMin, domainMin, domainMin];

          const s1Date =
            dateRange && dateRange[0]
              ? dateRange[0].clone().subtract(1, "week").add(i, "days")
              : null;
          const s2Date =
            dateRange && dateRange[0]
              ? dateRange[0].clone().add(i, "days")
              : null;

          const s1DateStr = formatDateStr(s1Date);
          const s2DateStr = formatDateStr(s2Date);

          const s1Tooltip = s1DateStr
            ? `${s1DateStr}\n${formatTooltip(s1Data[2])}`
            : `${formatTooltip(s1Data[2])}`;
          const s2Tooltip = s2DateStr
            ? `${s2DateStr}\n ${formatTooltip(s2Data[2])}`
            : `${formatTooltip(s2Data[2])}`;

          return (
            <div
              key={day}
              className="flex flex-col flex-1 gap-1 items-center h-full"
            >
              <div
                className="flex gap-1.5 flex-1 w-full items-end justify-center"
                style={{ height: "calc(100% - 18px)" }}
              >
                {showComparison && hasData(s1Data) && (
                  <div className="w-3 h-full relative">
                    <RangeBar
                      min={s1Data[0]}
                      max={s1Data[1]}
                      media={s1Data[2]}
                      domainMin={domainMin}
                      domainMax={domainMax}
                      color={color1}
                      tooltipText={s1Tooltip}
                    />
                  </div>
                )}
                {hasData(s2Data) && (
                  <div className="w-3 h-full relative">
                    <RangeBar
                      min={s2Data[0]}
                      max={s2Data[1]}
                      media={s2Data[2]}
                      domainMin={domainMin}
                      domainMax={domainMax}
                      color={color2}
                      tooltipText={s2Tooltip}
                    />
                  </div>
                )}
              </div>
              <span className="text-[9px] font-bold text-slate-400 text-center leading-none mt-1 select-none">
                {day}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MiniRangeChart;
